import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 4,
  },
  colors: {
    primary: {
      main: "#419468FF",
      dark: "#419468FF",
      contrastText: "#FFF",
    },
    secondary: {
      main: "#0E3A2FFF",
      dark: "#0E3A2FFF",
      contrastText: "#FFF",
    },
    error: {
      main: "#E82B37",
    },
    info: {
      main: "#2D71D7",
      light: "#DDE8F9",
    },
    text: {
      primary: "#161718",
      secondary: "#303132",
      hint: "#999999",
    },

    background: "#FFF",
    backgroundAlt: "#F1F1F1",
    background3: "#EAEAEA",
    border: "#7C7D7E",
    hover: "#464748",
    disabled: "#464748",
    placeholder: "#7C7D7E",
    transparent: "#00000000",
    divider: "#D8D8D8",
    dividerAlt: "#E6E6E6",
    tertiary: "#0E3A2FFF",
    primaryButon: "#78FAAE",
    primaryButonHover: "#A8FFCC",
  },
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const skoda = {
  themeName: "Skoda",
  brand: BrandsEnum.Skoda,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: 0,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    info: variables.colors.info,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundAlt,
      paper: variables.colors.background,
    },
    background: {
      default: "transparent",
      paper: variables.colors.background,
      statusbar: variables.colors.disabled,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
  },
  typography: {
    fontFamily: getFontFamily("Skoda-Next-Regular"),
    color: variables.colors.text.secondary,
    h1: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: "#000",
      fontWeight: 600,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
    },
    h2: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: "#000",
      fontWeight: 600,
      textAlign: "center" as "center",
      paddingBottom: "2rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h3: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 400,
      textDecoration: "underline",
    },
    h4: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      color: variables.colors.text.primary,
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      fontSize: "1.25rem",
      color: "#000",
      fontWeight: 600,
    },
    h6: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  paymentOption: {
    selectButton: {
      "& span": {
        color: variables.colors.text.primary,
        fontFamily: getFontFamily("Skoda-Next-Bold"),
      },
    },
    selectedPayment: {
      borderWidth: 2,
    },
  },
  radioButton: {
    borderRadius: 0,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: variables.colors.backgroundAlt,
    activeBorder: `2px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    addText: {
      color: variables.colors.text.primary,
      fontFamily: getFontFamily("Skoda-Next-Bold"),
    },
    checkmark: {
      display: "none",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      backgroundColor: variables.colors.background,
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      addText: {
        color: variables.colors.text.primary,
        fontFamily: getFontFamily("Skoda-Next-Bold"),
      },
    },
    header: {
      textColor: variables.colors.text.primary,
    },
    price: {
      fontFamily: getFontFamily("Skoda-Next-Bold"),
      alignSelf: "baseline",
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        border: `px solid ${variables.colors.primary.main}`,
        boxShadow: props.buttons.boxShadow,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
          color: variables.colors.border,
          borderColor: variables.colors.border,
        },
        borderRadius: 28,
        fontWeight: 700,
        "a&": {
          border: `1px solid ${variables.colors.primary.main}`,
          borderRadius: 28,
          "&:hover": {
            color: variables.colors.text.primary,
            backgroundColor: variables.colors.primaryButonHover,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            color: variables.colors.text.primary,
            backgroundColor: variables.colors.primaryButonHover,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.text.primary,
          backgroundColor: variables.colors.primaryButon,
          fontWeight: 700,
          textDecoration: "none",
        },
      },
      containedPrimary: {
        color: variables.colors.text.primary,
        backgroundColor: variables.colors.primaryButon,
        fontWeight: 700,
        textDecoration: "none",
        "&:hover": {
          backgroundColor: variables.colors.primaryButonHover,
        },
        "&:active": {
          backgroundColor: variables.colors.primaryButonHover,
        },
        "&$focusVisible": {
          backgroundColor: variables.colors.primaryButonHover,
        },
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
          color: variables.colors.border,
          borderColor: variables.colors.border,
        },
      },
      outlinedSecondary: {
        "&:hover": {
          backgroundColor: variables.colors.disabled,
        },
        "&:active": {
          backgroundColor: variables.colors.disabled,
        },
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
        },
        color: variables.colors.primary.contrastText,
        backgroundColor: variables.colors.text.secondary,
        borderRadius: theme.spacing(5),
        "a&": {
          "&:hover": {
            backgroundColor: variables.colors.disabled,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            backgroundColor: variables.colors.disabled,
            "& span": {
              textDecoration: "none",
            },
          },
          "&$disabled": {
            backgroundColor: variables.colors.disabled,
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.text.secondary,
          borderRadius: theme.spacing(5),
        },
      },
      textPrimary: {
        color: variables.colors.text.primary,
        fontFamily: getFontFamily("Skoda-Next-Bold"),
        "&:hover": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
        "&:active": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
      },
      text: {
        color: variables.colors.text.primary,
        "&:hover": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
        "&:active": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: variables.colors.border,
        },
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiInputLabel: {
      formControl: {
        position: "relative" as PositionProperty,
        transform: "none",
        color: variables.colors.text.primary,
        lineHeight: 1.75,
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: "4px 4px 0px 0px",
        borderWidth: 0,
        backgroundColor: variables.colors.backgroundAlt,
        "&:hover": {
          backgroundColor: variables.colors.backgroundAlt,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.placeholder,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.placeholder,
          borderWidth: 1,
        },
      },
      notchedOutline: {
        border: "none",
        borderBottom: `1px solid ${variables.colors.placeholder}`,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.secondary,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      root: {
        borderRadius: 4,
      },
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.backgroundAlt,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.backgroundAlt,
          },
        },
      },
    },
    MuiMenuItem: {
      root: {
        color: variables.colors.text.primary,
        border: `1px solid ${variables.colors.transparent}`,
        borderLeftWidth: 2,
        "&$selected": {
          backgroundColor: variables.colors.backgroundAlt,
        },
      },
    },
    MuiSelect: {
      root: {
        color: variables.colors.text.primary,
      },
      select: {
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        //...Helpers.svgSelectorWrapper(svgIcons.arrowDown, { svgRelation: '~' }),
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        color: variables.colors.primary.main,
      },
    },
    MuiList: {
      root: {
        border: `1px solid ${variables.colors.placeholder}`,
        borderRadius: 4,
      },
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          color: variables.colors.primary.main,
          textDecoration: "none",
          "&:hover": {
            color: variables.colors.text.primary,
          },
          "&:active": {
            color: variables.colors.text.hint,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        fontFamily: getFontFamily("Skoda-Next-Regular"),
        color: variables.colors.text.primary,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: 0,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: 0,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.text.primary,
        border: `1px solid ${variables.colors.border}`,
        "&$selected": {
          color: variables.colors.text.primary,
          borderWidth: 2,
          borderColor: variables.colors.primary.main,
          fontWeight: 600,
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.backgroundAlt,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.backgroundAlt,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  coverageSelect: {
    root: {
      borderColor: variables.colors.border,
      backgroundColor: variables.colors.background,
      textColor: variables.colors.border,
    },
    selected: {
      borderColor: variables.colors.primary.main,
      backgroundColor: variables.colors.background,
    },
    descriptionCard: {
      backgroundColor: variables.colors.background3,
      borderColor: variables.colors.background3,
    },
    recommended: {
      backgroundColor: variables.colors.primary.main,
      borderColor: "none",
      textColor: variables.colors.primary.contrastText,
    },
  },
  footerBar: {
    backgroundColor: variables.colors.tertiary,
    textColor: variables.colors.primary.contrastText,
  },
  header: {
    "& *": {
      color: variables.colors.text.primary,
    },
    backgroundColor: variables.colors.background,
    "& hr": {
      border: 0,
      backgroundColor: "transparent",
      margin: "8px 0",
    },
    "& h1": {
      margin: "0 auto",
    },
  },
  divider: {
    marginBottom: "1rem",
  },
  icons: {
    infoIcon: (
      <>
        <path d='M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z' />
      </>
    ),
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M11.5 20C13.7543 20 15.9163 19.1045 17.5104 17.5104C19.1045 15.9163 20 13.7543 20 11.5C20 9.24566 19.1045 7.08365 17.5104 5.48959C15.9163 3.89553 13.7543 3 11.5 3C9.24566 3 7.08365 3.89553 5.48959 5.48959C3.89553 7.08365 3 9.24566 3 11.5C3 13.7543 3.89553 15.9163 5.48959 17.5104C7.08365 19.1045 9.24566 20 11.5 20ZM21 11.5C21 12.7476 20.7543 13.9829 20.2769 15.1355C19.7994 16.2881 19.0997 17.3354 18.2175 18.2175C17.3354 19.0997 16.2881 19.7994 15.1355 20.2769C13.9829 20.7543 12.7476 21 11.5 21C10.2524 21 9.0171 20.7543 7.86451 20.2769C6.71191 19.7994 5.66464 19.0997 4.78249 18.2175C3.90033 17.3354 3.20056 16.2881 2.72314 15.1355C2.24573 13.9829 2 12.7476 2 11.5C2 8.98045 3.00089 6.56408 4.78248 4.78249C6.56408 3.00089 8.98044 2 11.5 2C14.0196 2 16.4359 3.00089 18.2175 4.78249C19.9991 6.56408 21 8.98044 21 11.5ZM12 9L12 16L11 16L11 9L12 9ZM12 8L12 7L11 7L11 8L12 8Z'
          fill='#419468'
        />
      </svg>
    ),

    addIcon: (
      <>
        <path fill='currentColor' d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' />
      </>
    ),

    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M3 11.5C3 9.24566 3.89553 7.08365 5.48959 5.48959C7.08365 3.89553 9.24566 3 11.5 3C13.7543 3 15.9163 3.89553 17.5104 5.48959C19.1045 7.08365 20 9.24566 20 11.5C20 13.7543 19.1045 15.9163 17.5104 17.5104C15.9163 19.1045 13.7543 20 11.5 20C9.24566 20 7.08365 19.1045 5.48959 17.5104C3.89553 15.9163 3 13.7543 3 11.5ZM11.5 2C10.2524 2 9.0171 2.24572 7.86451 2.72314C6.71191 3.20056 5.66464 3.90033 4.78249 4.78249C3.90033 5.66464 3.20056 6.71191 2.72314 7.86451C2.24572 9.0171 2 10.2524 2 11.5C2 12.7476 2.24572 13.9829 2.72314 15.1355C3.20056 16.2881 3.90033 17.3354 4.78249 18.2175C5.66464 19.0997 6.71191 19.7994 7.86451 20.2769C9.0171 20.7543 10.2524 21 11.5 21C14.0196 21 16.4359 19.9991 18.2175 18.2175C19.9991 16.4359 21 14.0196 21 11.5C21 8.98044 19.9991 6.56408 18.2175 4.78249C16.4359 3.00089 14.0196 2 11.5 2ZM9.854 14.854L16.354 8.354L15.646 7.646L9.5 13.793L7.354 11.646L6.646 12.354L9.146 14.854L9.5 15.207L9.854 14.854Z'
          fill='#419468'
        />
      </svg>
    ),

    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M12 12.2955L17.7045 18L18 17.7045L12.2955 12L18 6.2955L17.7045 6L12 11.7045L6.2955 6L6 6.2955L11.7045 12L6 17.7045L6.2955 18L12 12.2955Z'
            fill='#419468'
            stroke='#419468'
            stroke-width='0.8'
          />
        </g>
      </svg>
    ),

    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 12.2955L17.7045 18L18 17.7045L12.2955 12L18 6.2955L17.7045 6L12 11.7045L6.2955 6L6 6.2955L11.7045 12L6 17.7045L6.2955 18L12 12.2955Z'
          fill='#419468'
          stroke='#419468'
          stroke-width='0.8'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3 11.5C3 9.24566 3.89553 7.08365 5.48959 5.48959C7.08365 3.89553 9.24566 3 11.5 3C13.7543 3 15.9163 3.89553 17.5104 5.48959C19.1045 7.08365 20 9.24566 20 11.5C20 13.7543 19.1045 15.9163 17.5104 17.5104C15.9163 19.1045 13.7543 20 11.5 20C9.24566 20 7.08365 19.1045 5.48959 17.5104C3.89553 15.9163 3 13.7543 3 11.5ZM11.5 2C10.2524 2 9.0171 2.24572 7.86451 2.72314C6.71191 3.20056 5.66464 3.90033 4.78249 4.78249C3.90033 5.66464 3.20056 6.71191 2.72314 7.86451C2.24572 9.0171 2 10.2524 2 11.5C2 12.7476 2.24572 13.9829 2.72314 15.1355C3.20056 16.2881 3.90033 17.3354 4.78249 18.2175C5.66464 19.0997 6.71191 19.7994 7.86451 20.2769C9.0171 20.7543 10.2524 21 11.5 21C14.0196 21 16.4359 19.9991 18.2175 18.2175C19.9991 16.4359 21 14.0196 21 11.5C21 8.98044 19.9991 6.56408 18.2175 4.78249C16.4359 3.00089 14.0196 2 11.5 2ZM9.854 14.854L16.354 8.354L15.646 7.646L9.5 13.793L7.354 11.646L6.646 12.354L9.146 14.854L9.5 15.207L9.854 14.854Z" fill="#419468"/>
    </svg>
    `),
    checkmarkSelectedOptionIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M3 11.5C3 9.24566 3.89553 7.08365 5.48959 5.48959C7.08365 3.89553 9.24566 3 11.5 3C13.7543 3 15.9163 3.89553 17.5104 5.48959C19.1045 7.08365 20 9.24566 20 11.5C20 13.7543 19.1045 15.9163 17.5104 17.5104C15.9163 19.1045 13.7543 20 11.5 20C9.24566 20 7.08365 19.1045 5.48959 17.5104C3.89553 15.9163 3 13.7543 3 11.5ZM11.5 2C10.2524 2 9.0171 2.24572 7.86451 2.72314C6.71191 3.20056 5.66464 3.90033 4.78249 4.78249C3.90033 5.66464 3.20056 6.71191 2.72314 7.86451C2.24572 9.0171 2 10.2524 2 11.5C2 12.7476 2.24572 13.9829 2.72314 15.1355C3.20056 16.2881 3.90033 17.3354 4.78249 18.2175C5.66464 19.0997 6.71191 19.7994 7.86451 20.2769C9.0171 20.7543 10.2524 21 11.5 21C14.0196 21 16.4359 19.9991 18.2175 18.2175C19.9991 16.4359 21 14.0196 21 11.5C21 8.98044 19.9991 6.56408 18.2175 4.78249C16.4359 3.00089 14.0196 2 11.5 2ZM9.854 14.854L16.354 8.354L15.646 7.646L9.5 13.793L7.354 11.646L6.646 12.354L9.146 14.854L9.5 15.207L9.854 14.854Z'
          fill='#419468'
        />
      </svg>
    ),

    selectIcon: <path fill='currentColor' d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' />,

    backIcon: <path fill='currentColor' d='M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  priceText: {
    totalPriceColor: variables.colors.primary.contrastText,
    fontWeight: 700,
  },
  messageBox: {
    backgroundColor: "#DDE8F9",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  popup: {
    root: {
      fontFamily: getFontFamily("Skoda-Next-Regular"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#0E3A2F",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Skoda-Next-Regular"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  carInfo: {
    fontFamily: getFontFamily("Skoda-Next-Regular"),
    fontWeight: "bold",
  },

  CheckoutPage: {
    priceText: {
      color: variables.colors.text.primary,
    },
    topPriceText: {
      color: variables.colors.text.primary,
    },
  },

  outlinedButton: {
    "& span": {
      color: variables.colors.primary.contrastText,
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      "& span": {
        color: variables.colors.primary.contrastText,
      },
    },
    ConfirmationButtonContained: {
      color: variables.colors.text.primary,
      backgroundColor: variables.colors.primaryButon,
      fontWeight: 700,
      textDecoration: "none",
      "&:hover": {
        backgroundColor: variables.colors.primaryButonHover,
      },
      "&:active": {
        backgroundColor: variables.colors.primaryButonHover,
      },
      "&$focusVisible": {
        backgroundColor: variables.colors.primaryButonHover,
      },
      "&$disabled": {
        backgroundColor: variables.colors.disabled,
        color: variables.colors.border,
        borderColor: variables.colors.border,
      },
    },
  },
  StatusIndicator: {
    fontFamily: getFontFamily("Skoda-Next-Bold"),
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(skoda);
